(() => {
  const FORM_SELECTOR = '.ochaya-wholesale__enquiry-form';
  const STEP_SELECTOR = '[data-wholesale-step]';

  const initForm = (form) => {
    const stepRoot = form?.querySelector('[data-wholesale-step-form]');

    if (!form || !stepRoot || stepRoot.dataset.wholesaleStepReady === 'true') return;

    const steps = Array.from(stepRoot.querySelectorAll(STEP_SELECTOR));
    const progressWrap = stepRoot.querySelector('[data-wholesale-step-progress-wrap]');
    const progressBar = stepRoot.querySelector('[role="progressbar"]');
    const progressFill = stepRoot.querySelector('[data-wholesale-step-progress]');
    const counter = stepRoot.querySelector('[data-wholesale-step-count]');
    const stepLabel = stepRoot.dataset.stepLabel || 'Step';
    const stepOf = stepRoot.dataset.stepOf || 'of';
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let currentStep = 0;

    if (steps.length < 2) return;

    stepRoot.dataset.wholesaleStepReady = 'true';
    if (progressWrap) progressWrap.hidden = false;
    stepRoot.querySelectorAll('[data-wholesale-step-next], [data-wholesale-step-prev]').forEach((button) => {
      button.hidden = false;
    });

    const updateProgress = () => {
      const visibleStep = currentStep + 1;
      const percent = (visibleStep / steps.length) * 100;

      if (counter) counter.textContent = `${stepLabel} ${visibleStep} ${stepOf} ${steps.length}`;
      if (progressFill) progressFill.style.width = `${percent}%`;
      if (progressBar) {
        progressBar.setAttribute('aria-valuenow', String(visibleStep));
        progressBar.setAttribute('aria-valuetext', `${stepLabel} ${visibleStep} ${stepOf} ${steps.length}`);
      }
    };

    const focusActiveStep = (step) => {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          const heading = step.querySelector('[data-wholesale-step-heading]');
          const behavior = reducedMotion.matches ? 'auto' : 'smooth';

          heading?.focus({ preventScroll: true });
          step.scrollIntoView({ behavior, block: 'start' });
        });
      });
    };

    const setStep = (index, { focus = true } = {}) => {
      currentStep = Math.max(0, Math.min(index, steps.length - 1));

      steps.forEach((step, stepIndex) => {
        const isActive = stepIndex === currentStep;

        step.hidden = !isActive;
        step.classList.toggle('is-active', isActive);
        step.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        step.querySelectorAll('input, select, textarea').forEach((field) => {
          if (field.type !== 'hidden') field.disabled = !isActive;
        });
      });

      updateProgress();
      if (focus) focusActiveStep(steps[currentStep]);

      form.dispatchEvent(new CustomEvent('ochaya:wholesale-step-change', {
        bubbles: true,
        detail: { current: currentStep + 1, total: steps.length },
      }));
    };

    const activeStepIsValid = () => {
      const activeStep = steps[currentStep];
      const fields = Array.from(activeStep.querySelectorAll('input, select, textarea')).filter(
        (field) => !field.disabled && field.type !== 'hidden',
      );
      const invalidField = fields.find((field) => !field.checkValidity());

      if (!invalidField) return true;

      invalidField.setAttribute('aria-invalid', 'true');
      invalidField.focus({ preventScroll: false });
      invalidField.reportValidity();
      return false;
    };

    const revealStepValidation = (step) => {
      const fields = Array.from(step.querySelectorAll('input, select, textarea')).filter(
        (field) => field.type !== 'hidden',
      );
      const invalidField = fields.find((field) => !field.checkValidity());

      fields.forEach((field) => {
        if (!field.checkValidity()) field.setAttribute('aria-invalid', 'true');
      });

      if (!invalidField) return true;

      invalidField.focus({ preventScroll: false });
      invalidField.reportValidity();
      return false;
    };

    const allStepsAreValid = () => {
      let invalidStepIndex = -1;

      steps.forEach((step, stepIndex) => {
        step.querySelectorAll('input, select, textarea').forEach((field) => {
          field.disabled = false;
          if (field.type !== 'hidden' && !field.validity.valid && invalidStepIndex === -1) {
            invalidStepIndex = stepIndex;
          }
        });
      });

      if (invalidStepIndex === -1) return true;

      setStep(invalidStepIndex, { focus: false });
      revealStepValidation(steps[invalidStepIndex]);
      return false;
    };

    const guardStepSubmit = (event) => {
      if (event.target !== form) return;

      if (currentStep < steps.length - 1) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (activeStepIsValid()) setStep(currentStep + 1);
        return;
      }

      if (!allStepsAreValid()) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };

    stepRoot.addEventListener('click', (event) => {
      const nextButton = event.target.closest('[data-wholesale-step-next]');
      const previousButton = event.target.closest('[data-wholesale-step-prev]');

      if (!nextButton && !previousButton) return;

      event.preventDefault();

      if (nextButton) {
        if (activeStepIsValid()) setStep(currentStep + 1);
        return;
      }

      setStep(currentStep - 1);
    });

    stepRoot.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' || event.isComposing) return;
      if (event.target.matches('textarea, button, [type="submit"], [type="checkbox"], [type="radio"]')) return;
      if (currentStep >= steps.length - 1) return;

      event.preventDefault();
      if (activeStepIsValid()) setStep(currentStep + 1);
    });

    form.addEventListener('invalid', (event) => {
      const invalidStepIndex = steps.findIndex((step) => step.contains(event.target));

      if (invalidStepIndex >= 0 && invalidStepIndex !== currentStep) {
        setStep(invalidStepIndex, { focus: false });
      }
    }, true);

    document.addEventListener('submit', guardStepSubmit, true);

    setStep(0, { focus: false });
  };

  const init = (root = document) => {
    root.querySelectorAll(FORM_SELECTOR).forEach(initForm);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
